import React, { FC } from 'react';
import _isUndefined from 'lodash/isUndefined';
import { haveSameContents } from '@minecraft.utils';
import { Label, Checkmark } from './styles';

/** Types */
type Option = {
  name?: string;
  label?: string;
  value: string;
  count?: number;
  disabled?: boolean;
  labelComponent?: () => React.ReactNode;
  subText?: string;
};

type LabelComponentProps = {
  name: string;
  checked: boolean;
  option: Option;
  className?: string;
  onChange: (event: React.FormEvent<HTMLInputElement>, option: string) => void;
  disabled?: boolean;
  qaId?: string;
  hideCount?: boolean;
};

type Props = {
  name: string;
  value: string | Option;
  options: Option[];
  onChange: (event: React.FormEvent<HTMLInputElement>, option: string) => void;
  className?: string;
  'data-qa-id'?: string;
  hideCount?: boolean;
  radioGroupLabel: string;
};

const renderLabelText = (option, validation) => {
  return `${option?.label || ''}${validation ? ` (${option?.count})` : ''}`;
};

/** Help Component */
export const LabelComponent: FC<LabelComponentProps> = ({
  name,
  checked,
  option,
  className,
  onChange,
  qaId,
  disabled: commonDisabled,
  hideCount = false,
  ...props
}) => {
  const withCount = !_isUndefined(option?.count);
  const validation = withCount && !hideCount;

  return (
    <Label
      $disabled={option.disabled || commonDisabled}
      className={checked ? `${className} active` : className}
      data-qa-id={`${qaId}-${option.value}`}
      id={qaId}
    >
      {option.labelComponent && option.labelComponent()}
      {!option.labelComponent && renderLabelText(option, validation)}
      <input
        type="radio"
        checked={checked}
        onChange={(element): void => onChange(element, option.value)}
        name={name}
        value={option.value}
        disabled={option.disabled || commonDisabled || (withCount && option?.count <= 0)}
        aria-label={option?.label}
        {...props}
      />
      <Checkmark $disabled={option.disabled || commonDisabled} />
    </Label>
  );
};

/** Main Component */
/**
 * @deprecated This is an older component that does not use our standard form wrapper
 * to match the design system.
 *
 * If this component is used in application code, we want to replace it with a controlled
 * RadioGroupInput component instead
 *
 * use the RadioGroupInput
 * import { RadioGroupInput } from '@minecraft.molecules'
 *
 * <RadioGroupInput name="fieldName" label="Field Label" options={options} />
 *
 * for any props that are not currently implemented we should add the functionality
 */
const ControlledRadioGroup: FC<Props> = ({
  name,
  value: selectedValue,
  options,
  onChange,
  className,
  'data-qa-id': qaId,
  hideCount,
  radioGroupLabel,
  ...restProps
}) => (
  <div role="radiogroup" aria-label={radioGroupLabel}>
    {options.map((option) => {
      const calculatedSelectedValue =
        selectedValue && selectedValue.hasOwnProperty('value') ? selectedValue['value'] : selectedValue;
      const checked =
        option.value === selectedValue ||
        option.value === calculatedSelectedValue ||
        (Array.isArray(option.value) &&
          Array.isArray(calculatedSelectedValue) &&
          haveSameContents(option.value, calculatedSelectedValue));
      const key = String(option.value);

      return option.subText ? (
        <div className={className} key={key}>
          <LabelComponent
            hideCount={hideCount}
            name={name}
            checked={checked}
            option={option}
            onChange={onChange}
            className="label-with-sub"
            qaId={qaId}
            {...restProps}
          />
          <span className="radio-sub-text">{option.subText}</span>
        </div>
      ) : (
        <LabelComponent
          hideCount={hideCount}
          key={key}
          name={name}
          checked={checked}
          option={option}
          onChange={onChange}
          className={className}
          qaId={qaId}
          {...restProps}
        />
      );
    })}
  </div>
);

export default ControlledRadioGroup;
